import { defineMessages } from '@edx/frontend-platform/i18n';

const messages = defineMessages({
  errorMessage: {
    id: 'catalog.course-about.error-message',
    defaultMessage: 'If the problem persists, contact {supportEmail}.',
    description: 'Error page message.',
  },
  unavailableTitle: {
    id: 'catalog.course-about.unavailable-title',
    defaultMessage: 'Course unavailable',
    description: 'Page title when a course about page cannot be loaded.',
  },
  unavailableCardTitle: {
    id: 'catalog.course-about.unavailable-card-title',
    defaultMessage: 'Unable to load course page',
    description: 'Alert title when a course about page cannot be loaded.',
  },
  priceFree: {
    id: 'catalog.course-about.price-free',
    defaultMessage: 'Free',
    description: 'Label used when a course is free.',
  },
  pricePaid: {
    id: 'catalog.course-about.price-paid',
    defaultMessage: 'Paid',
    description: 'Label used when a course is paid.',
  },
  pacingInstructor: {
    id: 'catalog.course-about.pacing-instructor',
    defaultMessage: 'Instructor-paced',
    description: 'Pacing label for instructor-paced courses.',
  },
  pacingSelf: {
    id: 'catalog.course-about.pacing-self',
    defaultMessage: 'Self-paced',
    description: 'Pacing label for self-paced courses.',
  },
  pacingFlexible: {
    id: 'catalog.course-about.pacing-flexible',
    defaultMessage: 'Flexible pace',
    description: 'Fallback pacing label for courses with unspecified pacing.',
  },
  detailReference: {
    id: 'catalog.course-about.detail.reference',
    defaultMessage: 'Reference',
    description: 'Label for course reference detail.',
  },
  detailReferenceFallback: {
    id: 'catalog.course-about.detail.reference-fallback',
    defaultMessage: 'To be defined',
    description: 'Fallback value when a course reference is not available.',
  },
  detailStart: {
    id: 'catalog.course-about.detail.start',
    defaultMessage: 'Start',
    description: 'Label for course start date detail.',
  },
  detailStartFallback: {
    id: 'catalog.course-about.detail.start-fallback',
    defaultMessage: 'Date to be confirmed',
    description: 'Fallback value when the course start date is not confirmed.',
  },
  detailEnd: {
    id: 'catalog.course-about.detail.end',
    defaultMessage: 'End',
    description: 'Label for course end date detail.',
  },
  detailEffort: {
    id: 'catalog.course-about.detail.effort',
    defaultMessage: 'Recommended pace',
    description: 'Label for course effort detail.',
  },
  detailLanguage: {
    id: 'catalog.course-about.detail.language',
    defaultMessage: 'Language',
    description: 'Label for course language detail.',
  },
  detailAccess: {
    id: 'catalog.course-about.detail.access',
    defaultMessage: 'Access',
    description: 'Label for course access detail.',
  },
  actionUnavailableStatus: {
    id: 'catalog.course-about.action.unavailable-status',
    defaultMessage: 'Unavailable',
    description: 'Status label when course information is unavailable.',
  },
  actionUnavailableHelper: {
    id: 'catalog.course-about.action.unavailable-helper',
    defaultMessage: 'This course information is not ready yet.',
    description: 'Helper text when course information is unavailable.',
  },
  actionAccessCourseLabel: {
    id: 'catalog.course-about.action.access-course',
    defaultMessage: 'Access course',
    description: 'Primary action label to access an already enrolled course.',
  },
  actionAccessCourseStatus: {
    id: 'catalog.course-about.action.access-course-status',
    defaultMessage: 'Active enrollment',
    description: 'Status label when the learner is already enrolled.',
  },
  actionAccessCourseHelper: {
    id: 'catalog.course-about.action.access-course-helper',
    defaultMessage: 'Your session is already ready. Resume directly in the learning space.',
    description: 'Helper text when the learner is already enrolled.',
  },
  actionViewCourseLabel: {
    id: 'catalog.course-about.action.view-course',
    defaultMessage: 'View course',
    description: 'Primary action label for public course access.',
  },
  actionViewCourseStatus: {
    id: 'catalog.course-about.action.view-course-status',
    defaultMessage: 'Public access',
    description: 'Status label for public course access.',
  },
  actionViewCourseHelper: {
    id: 'catalog.course-about.action.view-course-helper',
    defaultMessage: 'Some content is available immediately.',
    description: 'Helper text for public course access.',
  },
  actionCourseFullStatus: {
    id: 'catalog.course-about.action.course-full-status',
    defaultMessage: 'Session full',
    description: 'Status label when the course is full.',
  },
  actionCourseFullHelper: {
    id: 'catalog.course-about.action.course-full-helper',
    defaultMessage: 'All available seats are currently occupied.',
    description: 'Helper text when the course is full.',
  },
  actionInvitationOnlyStatus: {
    id: 'catalog.course-about.action.invitation-only-status',
    defaultMessage: 'By invitation',
    description: 'Status label when the course is invitation only.',
  },
  actionInvitationOnlyHelper: {
    id: 'catalog.course-about.action.invitation-only-helper',
    defaultMessage: 'Access to this session is by invitation only.',
    description: 'Helper text when the course is invitation only.',
  },
  actionBuyAccessLabel: {
    id: 'catalog.course-about.action.buy-access',
    defaultMessage: 'Buy access',
    description: 'Primary action label for a paid course.',
  },
  actionPaidStatus: {
    id: 'catalog.course-about.action.paid-status',
    defaultMessage: 'Paid access',
    description: 'Status label for a paid course.',
  },
  actionPaidHelper: {
    id: 'catalog.course-about.action.paid-helper',
    defaultMessage: 'Complete purchase, then access the program immediately.',
    description: 'Helper text for a paid course.',
  },
  actionClosedStatus: {
    id: 'catalog.course-about.action.closed-status',
    defaultMessage: 'Enrollment closed',
    description: 'Status label when enrollment is closed.',
  },
  actionClosedHelper: {
    id: 'catalog.course-about.action.closed-helper',
    defaultMessage: 'Enrollment is not open at the moment.',
    description: 'Helper text when enrollment is closed.',
  },
  actionEnrollPendingLabel: {
    id: 'catalog.course-about.action.enroll-pending',
    defaultMessage: 'Enrolling...',
    description: 'Primary action label while enrollment is pending.',
  },
  actionEnrollLabel: {
    id: 'catalog.course-about.action.enroll',
    defaultMessage: 'Enroll',
    description: 'Primary action label when enrollment is open.',
  },
  actionEnrollStatus: {
    id: 'catalog.course-about.action.enroll-status',
    defaultMessage: 'Enrollment open',
    description: 'Status label when enrollment is open.',
  },
  actionEnrollHelper: {
    id: 'catalog.course-about.action.enroll-helper',
    defaultMessage: 'Start now and find your progress in your dashboard.',
    description: 'Helper text when enrollment is open.',
  },
  backToCatalog: {
    id: 'catalog.course-about.back-to-catalog',
    defaultMessage: 'Back to catalog',
    description: 'Back link from the course about page to the catalog.',
  },
  heroEyebrow: {
    id: 'catalog.course-about.hero-eyebrow',
    defaultMessage: 'Course page',
    description: 'Eyebrow label on the course about page hero.',
  },
  exploreOtherCourses: {
    id: 'catalog.course-about.explore-other-courses',
    defaultMessage: 'Explore other courses',
    description: 'Secondary action label to return to the course listing.',
  },
  heroErrorTitle: {
    id: 'catalog.course-about.hero-error-title',
    defaultMessage: 'An error occurred',
    description: 'Alert title for enrollment or course action errors.',
  },
  watchPresentation: {
    id: 'catalog.course-about.watch-presentation',
    defaultMessage: 'View presentation',
    description: 'Label for the course introduction video CTA.',
  },
  overviewEyebrow: {
    id: 'catalog.course-about.overview-eyebrow',
    defaultMessage: 'Overview',
    description: 'Eyebrow label above the course overview section.',
  },
  overviewTitle: {
    id: 'catalog.course-about.overview-title',
    defaultMessage: 'Program overview',
    description: 'Title of the course overview section.',
  },
  overviewEmpty: {
    id: 'catalog.course-about.overview-empty',
    defaultMessage: 'The detailed presentation for this course will be published soon.',
    description: 'Fallback text when no detailed overview is available.',
  },
  requirementsEyebrow: {
    id: 'catalog.course-about.requirements-eyebrow',
    defaultMessage: 'Preparation',
    description: 'Eyebrow label above the requirements section.',
  },
  requirementsTitle: {
    id: 'catalog.course-about.requirements-title',
    defaultMessage: 'Technical prerequisites',
    description: 'Title of the requirements section.',
  },
  prerequisitesEyebrow: {
    id: 'catalog.course-about.prerequisites-eyebrow',
    defaultMessage: 'Before you start',
    description: 'Eyebrow label above the prerequisites section.',
  },
  prerequisitesTitle: {
    id: 'catalog.course-about.prerequisites-title',
    defaultMessage: 'Recommended knowledge',
    description: 'Title of the prerequisites section.',
  },
  decisionEyebrow: {
    id: 'catalog.course-about.decision-eyebrow',
    defaultMessage: 'Decision',
    description: 'Eyebrow label above the access decision card.',
  },
  decisionTitle: {
    id: 'catalog.course-about.decision-title',
    defaultMessage: 'Access and enrollment',
    description: 'Title of the access decision card.',
  },
  otherSessions: {
    id: 'catalog.course-about.other-sessions',
    defaultMessage: 'View other sessions',
    description: 'Secondary action label to browse other sessions.',
  },
  keyInfoEyebrow: {
    id: 'catalog.course-about.key-info-eyebrow',
    defaultMessage: 'Markers',
    description: 'Eyebrow label above the key information card.',
  },
  keyInfoTitle: {
    id: 'catalog.course-about.key-info-title',
    defaultMessage: 'Key information',
    description: 'Title of the key information card.',
  },
  supportEyebrow: {
    id: 'catalog.course-about.support-eyebrow',
    defaultMessage: 'Support',
    description: 'Eyebrow label above the support card.',
  },
  supportTitle: {
    id: 'catalog.course-about.support-title',
    defaultMessage: 'Need help?',
    description: 'Title of the support card.',
  },
  supportSummary: {
    id: 'catalog.course-about.support-summary',
    defaultMessage: 'Our team can help you choose the right session or answer questions before enrollment.',
    description: 'Summary text inside the support card.',
  },
  supportAction: {
    id: 'catalog.course-about.support-action',
    defaultMessage: 'Contact team',
    description: 'Button label in the support card.',
  },
  legacyStaffFallbackName: {
    id: 'catalog.course-about.legacy-staff-fallback-name',
    defaultMessage: 'Instructor {index}',
    description: 'Fallback label for a course staff member when no name is available in legacy overview HTML.',
  },
  viewAboutPageInStudio: {
    id: 'catalog.course-about.view-about-page-in-studio',
    defaultMessage: 'View presentation page in Studio',
    description: 'Link to view the Schedule and Details page in Studio.',
  },
});

export default messages;
